import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { readingService } from '../lib/reading-service';
import { useFamilyContext } from './FamilyContext';

// Only log in development to improve production performance
const isDev = import.meta.env.MODE === 'development';
const devLog = (...args) => isDev && console.log(...args);
const devWarn = (...args) => isDev && console.warn(...args);
const devError = (...args) => console.error(...args); // Always show errors

// Create the context
const ReadingContext = createContext();

// Create the hook
export const useReadingContext = () => {
  const context = useContext(ReadingContext);
  if (!context) {
    throw new Error('useReadingContext must be used within ReadingProvider');
  }
  return context;
};

// Create a safe version that doesn't throw when context is not available
export const useReadingContextSafe = () => {
  const context = useContext(ReadingContext);
  return context; // Returns null if not wrapped in provider
};

// Notification helpers
const showToast = (message, type = 'success') => {
  // Integration point for toast notifications
  devLog(`📢 ${type.toUpperCase()}: ${message}`);
  
  // If using a toast library like react-hot-toast:
  // if (type === 'success') toast.success(message);
  // else if (type === 'error') toast.error(message);
  // else toast(message);
};

// Mock data removed - using real data from service layer

// Create the provider component
export const ReadingProvider = ({ children }) => {
  const { currentUser, family } = useFamilyContext();
  
  // Core reading state - using real data from service layer
  const [currentBook, setCurrentBook] = useState(null);
  const [readingStats, setReadingStats] = useState({ booksRead: 0, currentStreak: 0, totalEarned: 0, pagesReadToday: 0, averagePagesPerDay: 0 });
  const [rewards, setRewards] = useState(null);
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [familyRecommendations, setFamilyRecommendations] = useState([]);
  const [achievements, setAchievements] = useState({ earned: [], available: [] });
  const [readingList, setReadingList] = useState([]);
  
  // UI state
  const [isLoading, setIsLoading] = useState(true); // Start with true to load real data
  const [error, setError] = useState(null);
  const [isBookSwitcherOpen, setIsBookSwitcherOpen] = useState(false);
  
  // Performance tracking
  const lastLoadTime = useRef(0);
  const loadingRef = useRef(false);

  // Load all reading data
  const loadReadingData = useCallback(async (forceRefresh = false) => {
    if (!currentUser?.id) {
      devLog('👤 No current user, skipping reading data load');
      return;
    }

    // Prevent duplicate loading
    if (loadingRef.current && !forceRefresh) {
      devLog('🔄 Already loading, skipping duplicate request');
      return;
    }

    // Rate limiting - don't reload more than once per 2 seconds
    const now = Date.now();
    if (!forceRefresh && (now - lastLoadTime.current) < 2000) {
      devLog('⏰ Rate limiting: skipping reload');
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    setError(null);
    lastLoadTime.current = now;
    
    try {
      devLog('📚 Loading reading data for user:', currentUser.id);
      
      // Load all data in parallel for better performance
      const [
        currentSession,
        stats,
        rewardsData,
        challenge,
        recommendations,
        userAchievements,
        userReadingList
      ] = await Promise.all([
        readingService.getCurrentReadingSession(currentUser.id),
        readingService.getReadingStats(currentUser.id),
        readingService.getReadingRewards(currentUser.id),
        readingService.getActiveChallenge(currentUser.id),
        readingService.getFamilyRecommendations(currentUser.id),
        readingService.getUserAchievements(currentUser.id),
        readingService.getUserReadingList(currentUser.id)
      ]);

      // Update all state
      setCurrentBook(currentSession);
      setReadingStats(stats);
      setRewards(rewardsData);
      setActiveChallenge(challenge);
      setFamilyRecommendations(recommendations);
      setAchievements(userAchievements);
      setReadingList(userReadingList);
      
      devLog('✅ Reading data loaded successfully');
    } catch (error) {
      devError('❌ Error loading reading data:', error);
      setError(error.message);
      showToast('Failed to load reading data', 'error');
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [currentUser?.id]);

  // Update reading progress
  const updateProgress = useCallback(async (sessionId, newPage) => {
    if (!sessionId || newPage === undefined) return;
    
    try {
      devLog('📖 Updating reading progress:', { sessionId, newPage });
      
      const updatedSession = await readingService.updateReadingProgress(sessionId, newPage);
      setCurrentBook(updatedSession);
      
      // Refresh stats to get updated progress
      const updatedStats = await readingService.getReadingStats(currentUser.id);
      setReadingStats(updatedStats);
      
      showToast('Progress updated! 🌟');
      return updatedSession;
    } catch (error) {
      devError('❌ Error updating progress:', error);
      showToast('Failed to update progress', 'error');
      throw error;
    }
  }, [currentUser?.id]);

  // Complete current book
  const completeBook = useCallback(async (sessionId) => {
    if (!sessionId) return;
    
    try {
      devLog('🎉 Completing book:', sessionId);
      
      const completedSession = await readingService.completeBook(sessionId);
      
      // Refresh all data after book completion
      await loadReadingData(true);
      
      showToast('🎉 Book completed! Great job!');
      return completedSession;
    } catch (error) {
      devError('❌ Error completing book:', error);
      showToast('Failed to complete book', 'error');
      throw error;
    }
  }, [loadReadingData]);

  // Start reading a new book
  const startNewBook = useCallback(async (bookData) => {
    if (!currentUser?.id || !bookData) return;
    
    try {
      devLog('📚 Starting new book:', bookData);
      
      const newSession = await readingService.createReadingSession(currentUser.id, bookData);
      setCurrentBook(newSession);
      
      // Add to reading list if not already there
      const isInList = readingList.some(book => 
        book.book_title === bookData.title && book.book_author === bookData.author
      );
      
      if (!isInList) {
        await readingService.addBookToReadingList(currentUser.id, bookData);
        const updatedList = await readingService.getUserReadingList(currentUser.id);
        setReadingList(updatedList);
      }
      
      showToast(`Started reading "${bookData.title}"! 📖`);
      return newSession;
    } catch (error) {
      devError('❌ Error starting new book:', error);
      showToast('Failed to start new book', 'error');
      throw error;
    }
  }, [currentUser?.id, readingList]);

  // Add book to reading list
  const addToReadingList = useCallback(async (bookData) => {
    if (!currentUser?.id || !bookData) return;
    
    try {
      devLog('📋 Adding book to reading list:', bookData);
      
      await readingService.addBookToReadingList(currentUser.id, bookData);
      const updatedList = await readingService.getUserReadingList(currentUser.id);
      setReadingList(updatedList);
      
      showToast('Added to your reading list! 📚');
    } catch (error) {
      devError('❌ Error adding to reading list:', error);
      showToast('Failed to add to reading list', 'error');
      throw error;
    }
  }, [currentUser?.id]);

  // Handle recommendation actions
  const handleRecommendationAction = useCallback(async (recommendationId, action, bookData) => {
    try {
      devLog('💡 Handling recommendation action:', { recommendationId, action });
      
      switch (action) {
        case 'add_to_list':
          await addToReadingList(bookData);
          await readingService.updateRecommendationStatus(recommendationId, 'added_to_list');
          break;
        case 'add_to_shopping':
          await readingService.addBookToShoppingList(currentUser.id, bookData);
          showToast('Added to family shopping list! 🛒');
          break;
        case 'ask_recommender':
          if (bookData.recommended_by_id) {
            await readingService.sendBookDiscussionMessage(
              currentUser.id,
              bookData.recommended_by_id,
              bookData.book_title
            );
            showToast('Message sent! 💬');
          }
          break;
      }
      
      // Refresh recommendations
      const updatedRecommendations = await readingService.getFamilyRecommendations(currentUser.id);
      setFamilyRecommendations(updatedRecommendations);
    } catch (error) {
      devError('❌ Error handling recommendation action:', error);
      showToast('Action failed', 'error');
      throw error;
    }
  }, [currentUser?.id, addToReadingList]);

  // Search for books
  const searchBooks = useCallback(async (query) => {
    if (!query.trim()) return [];
    
    try {
      devLog('🔍 Searching books:', query);
      return await readingService.searchBooks(query);
    } catch (error) {
      devError('❌ Error searching books:', error);
      showToast('Search failed', 'error');
      return [];
    }
  }, []);

  // Book switcher controls
  const openBookSwitcher = useCallback(() => {
    setIsBookSwitcherOpen(true);
  }, []);

  const closeBookSwitcher = useCallback(() => {
    setIsBookSwitcherOpen(false);
  }, []);

  // Load data when user changes
  useEffect(() => {
    if (currentUser?.id) {
      loadReadingData();
    } else {
      // Reset state when no user
      setCurrentBook(null);
      setReadingStats({ booksRead: 0, currentStreak: 0, totalEarned: 0, pagesReadToday: 0, averagePagesPerDay: 0 });
      setRewards(null);
      setActiveChallenge(null);
      setFamilyRecommendations([]);
      setAchievements({ earned: [], available: [] });
      setReadingList([]);
      setIsLoading(false);
    }
  }, [currentUser?.id, loadReadingData]);

  // Create context value
  const contextValue = {
    // Core reading data
    currentBook,
    readingStats,
    rewards,
    activeChallenge,
    familyRecommendations,
    achievements,
    readingList,
    
    // UI state
    isLoading,
    error,
    isBookSwitcherOpen,
    
    // Actions
    loadReadingData,
    updateProgress,
    completeBook,
    startNewBook,
    addToReadingList,
    handleRecommendationAction,
    searchBooks,
    
    // UI controls
    openBookSwitcher,
    closeBookSwitcher,
    
    // Utilities
    showToast
  };

  return (
    <ReadingContext.Provider value={contextValue}>
      {children}
    </ReadingContext.Provider>
  );
};

export default ReadingProvider;